import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PortalHeader, PortalLoading } from '../components/PortalComponents';
import { Button } from '../components/ui/button';
import { ScrollToTop } from '../components/ScrollToTop';
import { SubscriptionService, MANKLAR_SERVICE_INFO } from '../services/mockServices';
import { 
  CreditCard, Calendar, Check, Star, 
  Zap, Shield, Headphones, RefreshCw, Info
} from 'lucide-react';

function StatusBadge({ status }) {
  const config = {
    active: { label: 'Activo', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    pending: { label: 'Pendiente', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    suspended: { label: 'Suspendido', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
    cancelled: { label: 'Cancelado', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' }
  };
  const c = config[status] || config.active;
  return <span className={`px-3 py-1 text-xs font-medium rounded-full border ${c.color}`}>{c.label}</span>;
}

export default function SubscriptionPage() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await SubscriptionService.getSubscription();
        setSubscription(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) {
    return <PortalLoading />;
  }

  return (
    <div className="min-h-screen bg-[#030303]">
      <PortalHeader title="Mi Suscripción" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-light text-white font-['Outfit']">Mi Suscripción</h1>
          <p className="text-sm text-zinc-500 mt-1 font-['Manrope']">Detalles de tu plan Manklar</p>
        </div>

        {/* Subscription Card */}
        <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-amber-400" />
                <span className="text-xs uppercase tracking-wider text-amber-400 font-['Manrope']">Tu equipo</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-medium text-white font-['Outfit']">{subscription?.planName}</h2>
              <p className="text-sm text-zinc-400 mt-1 font-['Manrope']">{subscription?.planDescription}</p>
            </div>
            <StatusBadge status={subscription?.status} />
          </div>

          <div className="flex items-baseline gap-1 mb-6">
            <span className="text-4xl sm:text-5xl font-light text-white font-['Outfit']">
              {subscription?.monthlyPrice?.toFixed(2)}€
            </span>
            <span className="text-zinc-500 font-['Manrope']">/ mes</span>
            <span className="text-xs text-zinc-500 ml-2 font-['Manrope']">(IVA incluido para hogar)</span>
          </div>

          {/* Equipment Features */}
          {subscription?.equipmentFeatures && (
            <div className="mb-6 p-4 rounded-xl bg-white/5">
              <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-['Manrope']">Características del equipo</p>
              <div className="flex flex-wrap gap-2">
                {subscription.equipmentFeatures.map((feature) => (
                  <span key={feature} className="px-3 py-1 rounded-full bg-white/10 text-xs text-zinc-300 font-['Manrope']">
                    {feature}
                  </span>
                ))}
              </div>
              {subscription.coldTankCapacity && (
                <p className="text-xs text-zinc-400 mt-2 font-['Manrope']">Depósito agua fría: {subscription.coldTankCapacity}</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 rounded-xl bg-white/5">
              <p className="text-xs text-zinc-500 mb-1 font-['Manrope']">Fecha de inicio</p>
              <p className="text-sm text-white font-['Manrope']">{new Date(subscription?.startDate).toLocaleDateString('es-ES')}</p>
            </div>
            <div className="p-4 rounded-xl bg-white/5">
              <p className="text-xs text-zinc-500 mb-1 font-['Manrope']">Próxima facturación</p>
              <p className="text-sm text-white font-['Manrope']">{new Date(subscription?.nextBillingDate).toLocaleDateString('es-ES')}</p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-white/5 flex items-center gap-3 mb-4">
            <CreditCard className="w-5 h-5 text-zinc-400" />
            <div>
              <p className="text-xs text-zinc-500 font-['Manrope']">Método de pago</p>
              <p className="text-sm text-white font-['Manrope']">{subscription?.paymentMethod}</p>
            </div>
          </div>

          {subscription?.depositPaid && (
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3">
              <Check className="w-5 h-5 text-emerald-400" />
              <div>
                <p className="text-xs text-emerald-400 font-['Manrope']">Fianza abonada</p>
                <p className="text-sm text-white font-['Manrope']">{subscription?.deposit?.toFixed(2)}€</p>
              </div>
            </div>
          )}
        </div>

        {/* Features - Service Included */}
        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10 mb-6">
          <h3 className="text-sm uppercase tracking-wider text-zinc-500 mb-4 font-['Manrope']">Incluido en tu suscripción</h3>
          <div className="space-y-3">
            {subscription?.features?.map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-sm text-zinc-300 font-['Manrope']">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Warranty Info */}
        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/10 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-base font-medium text-white mb-2 font-['Outfit']">Garantía Manklar</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-3 font-['Manrope']">
                {subscription?.warranty?.description}
              </p>
              <div className="p-3 rounded-lg bg-white/5">
                <p className="text-xs text-zinc-400 font-['Manrope']">
                  <span className="text-white font-medium">Compromiso de resolución:</span> Si comunicas una avería y no queda resuelta en un plazo máximo de <span className="text-emerald-400 font-medium">{subscription?.warranty?.incidentResolutionTime}</span>, se te abonará la siguiente mensualidad del equipo afectado.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Maintenance Info */}
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 mb-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-400 font-medium mb-1 font-['Outfit']">Sobre el mantenimiento</p>
              <p className="text-xs text-zinc-400 font-['Manrope']">
                El mantenimiento preventivo anual está incluido en tu cuota. Si necesitas un mantenimiento adicional antes de cumplir el año, el coste será de <span className="text-white font-medium">59€</span>.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Link to="/billing">
            <Button variant="outline" className="w-full h-12 border-white/20 text-white hover:bg-white/10 rounded-xl">
              Ver historial de facturación
            </Button>
          </Link>
          <Link to="/help">
            <Button variant="outline" className="w-full h-12 border-white/20 text-zinc-400 hover:text-white hover:bg-white/10 rounded-xl">
              ¿Tienes dudas? Centro de ayuda
            </Button>
          </Link>
        </div>
      </main>

      {/* Scroll to Top Button */}
      <ScrollToTop />
    </div>
  );
}
